import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { NgSelectModule } from '@ng-select/ng-select';
import {
    catchError, concat, debounceTime, distinctUntilChanged, filter, map,
    Observable, of, startWith, Subject, switchMap, tap
} from 'rxjs';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { FormatSearchTermPipePipe } from '../../common/pipes/format-search-term/format-search-term-pipe.pipe';
import { SanitizeUrlHostPipe } from '../../common/pipes/sanitize-url-host/sanitize-url-host.pipe';
import { OrganizationSearchService } from '../../common/services/organization-search.service';
import { OrganizationDto } from '../../common/interfaces/organization-dto';

@Component({
    selector: 'lib-organization-search',
    standalone: true,
    imports: [NgSelectModule,
        FormsModule,
        ReactiveFormsModule,
        CommonModule,
        FormatSearchTermPipePipe,
        SanitizeUrlHostPipe],
    templateUrl: './organization-search.component.html',
    styleUrl: './organization-search.component.scss'
})
export class OrganizationSearchComponent implements OnChanges {
    private organizationService = inject(OrganizationSearchService);

    currentSearchTerm = '';
    minSearchTermLength = 3;
    organizationsLoading = false;
    morePages = false;
    organizationsSearchInput$ = new Subject<string>;
    organizations$: Observable<OrganizationDto[]> = new Observable<OrganizationDto[]>();

    @Input() readOnly = false;
    @Input() selectedOrganizations: OrganizationDto[] = new Array<OrganizationDto>();
    @Output() newOrganizationSelectedEvent = new EventEmitter<OrganizationDto>();
    selectedOrganization = 'Any';
    @Input() reset = false;
    @Input() enterpriseId = 0;
    @Input() truncateText = false;


    loadOrganizations(): void {
        this.organizations$ = concat(
            this.organizationsSearchInput$.pipe(
                filter(currentSearchTerm => this.checkSearchTermLength(currentSearchTerm)),
                startWith(''),
                distinctUntilChanged(),
                debounceTime(800),
                tap(() => this.organizationsLoading = true),
                switchMap(searchTerm => {
                    const termToSearch = searchTerm ?? '';
                    return this.organizationService.searchActive(termToSearch || '', this.enterpriseId).pipe(
                        map(organizationsResult => {
                            this.morePages = organizationsResult.totalElements > organizationsResult.size;
                            return organizationsResult.content; // Return the organizations array
                        }),
                        catchError(() => of([])), // In case of an error, return an empty array
                        tap(() => this.organizationsLoading = false) // Indicate loading finished
                    );
                })
            )
        );
    }

    private checkSearchTermLength(searchTerm: string): boolean {
        return searchTerm !== null && (searchTerm.length === 0 || searchTerm.length >= this.minSearchTermLength);
    }

    onOrganizationChange(event: OrganizationDto): void {
        if (event) {
            this.newOrganizationSelectedEvent.emit(event);
            this.selectedOrganization = event.name;
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSearchTermChange(event: { term: string; items: any[] }): void {
        if (!event.term) {
            this.resetCurrentSearch();
            return;
        }
        if (this.currentSearchTerm !== event.term) {
            this.currentSearchTerm = event.term;
        }
    }

    resetCurrentSearch(): void {
        this.loadOrganizations();
        this.morePages = false;
        this.selectedOrganization = 'Any';
        this.newOrganizationSelectedEvent.emit(undefined);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes[ 'reset' ]) {
            this.resetCurrentSearch();
        }
    }

    onDropdownOpen() {
        this.organizationsSearchInput$.next('');
        this.loadOrganizations();
        this.selectedOrganization = '';
    }

    onDropdownClose() {
        if (this.selectedOrganization === '') {
            this.selectedOrganization = 'Any';
            this.newOrganizationSelectedEvent.emit(undefined);
        }
    }
}
